// CareBot 开通转化 Service
// 复用 apiUserService.ts 的 fetch + ApiResponse 解包模式
// 通过 VITE_USE_CAREBOT_MOCK 切换 mock / 真实 Rails 接口
//
// 真实接口（待 Rails 实现）：
//   POST /api/carebot/onboard          顾问触发开通
//   POST /api/carebot/family-invite    顾问代发家属邀请

import { apiConfigManager } from '../config/apiConfig';
import { authService } from './authService';
import {
  CareBotFamilyInviteRequest,
  CareBotFamilyInviteResponse,
  CareBotOnboardingError,
  CareBotOnboardingStatus,
  CareBotOnboardRequest,
  CareBotOnboardResponse,
  CareBotSendSmsResponse,
} from '../types/carebotOnboarding';
import {
  buildMockFamilyInviteResponse,
  buildMockOnboardResponse,
  mockDelay,
} from '../utils/carebotMockData';

interface ApiResponse<T = unknown> {
  success: boolean;
  data?: T;
  message?: string;
  error?: string;
  error_code?: string;
  timestamp?: string;
}

// Vite 环境变量（局部声明，避免污染全局 ImportMeta 类型）
type CareBotEnv = {
  VITE_USE_CAREBOT_MOCK?: string;
  VITE_CAREBOT_FAMILY_INVITE_ENABLED?: string;
};

function readEnv(): CareBotEnv {
  return (import.meta as unknown as { env?: CareBotEnv }).env ?? {};
}

// 业务错误码 → 中文文案（顾问可读）
const ERROR_CODE_MESSAGES: Record<string, string> = {
  forbidden: '当前账号无权操作此用户档案',
  assessment_not_found: '找不到对应的评估记录',
  missing_elder_phone: '请先在用户档案中补充老人手机号',
  missing_elder_phone_or_sms_code: '请同时提供手机号与短信验证码',
  invalid_phone: '老人手机号格式不正确',
  invalid_sms_code: '验证码无效或已过期，请重新获取',
  sms_rate_limited: '验证码发送过于频繁，请 1 分钟后重试',
  sms_send_failed: '短信发送失败，请稍后重试',
  consultant_mapping_missing: '当前顾问尚未配置 CareBot 映射，请联系管理员',
  carebot_unavailable: 'CareBot 服务暂时不可用，请稍后重试',
  invalid_signature: 'CareBot 验签失败，请联系管理员',
  network_error: '网络异常，请检查连接后重试',
  invalid_response: '服务端响应格式异常，请联系开发人员',
};

function resolveErrorMessage(code: string | undefined, fallback?: string): string {
  if (code && ERROR_CODE_MESSAGES[code]) return ERROR_CODE_MESSAGES[code];
  if (fallback) return fallback;
  return '操作失败，请稍后重试';
}

// 简单守卫：避免 Rails 字段名漂移导致下游静默崩
function assertOnboardShape(data: unknown): asserts data is CareBotOnboardResponse {
  const claim = (data as { elderClaim?: { shortCode?: unknown; claimUrl?: unknown } } | null)
    ?.elderClaim;
  if (!claim || typeof claim.shortCode !== 'string' || typeof claim.claimUrl !== 'string') {
    throw new CareBotOnboardingError(
      'invalid_response',
      ERROR_CODE_MESSAGES.invalid_response,
      false
    );
  }
}

function assertFamilyInviteShape(
  data: unknown
): asserts data is CareBotFamilyInviteResponse {
  const invite = (data as { familyInvite?: { shortCode?: unknown; inviteUrl?: unknown } } | null)
    ?.familyInvite;
  if (!invite || typeof invite.shortCode !== 'string' || typeof invite.inviteUrl !== 'string') {
    throw new CareBotOnboardingError(
      'invalid_response',
      ERROR_CODE_MESSAGES.invalid_response,
      false
    );
  }
}

class CarebotOnboardingService {
  private readonly env = readEnv();

  // 默认 mock 开启；显式设 'false' 切真实接口
  private readonly useMock: boolean = this.env.VITE_USE_CAREBOT_MOCK !== 'false';

  isMockMode(): boolean {
    return this.useMock;
  }

  /**
   * Family invite endpoint 开关
   * - 真实模式（Rails）下：默认关闭，避免 Rails BFF 未实现 /api/carebot/family-invite 时 404
   *   → 由运维显式 `VITE_CAREBOT_FAMILY_INVITE_ENABLED=true` 在 endpoint 就绪后开启
   * - mock 模式：默认开启，便于 UI/UX 演示
   * - 任意模式可被显式 'true'/'false' 覆盖
   */
  isFamilyInviteEnabled(): boolean {
    const flag = this.env.VITE_CAREBOT_FAMILY_INVITE_ENABLED;
    if (flag === 'true') return true;
    if (flag === 'false') return false;
    return this.useMock;
  }

  private async request<T = unknown>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const url = apiConfigManager.buildApiUrl(endpoint);
    const authHeaders = authService.getAuthHeaders();

    let response: Response;
    try {
      response = await fetch(url, {
        ...options,
        headers: { ...authHeaders, ...options.headers },
      });
    } catch (err) {
      if (err instanceof TypeError) {
        throw new CareBotOnboardingError(
          'network_error',
          ERROR_CODE_MESSAGES.network_error
        );
      }
      throw err;
    }

    let body: ApiResponse<T> | null = null;
    try {
      body = (await response.json()) as ApiResponse<T>;
    } catch {
      // 非 JSON 响应
    }

    if (!response.ok || !body || body.success === false) {
      const code = body?.error_code;
      const message = resolveErrorMessage(code, body?.error || body?.message);
      throw new CareBotOnboardingError(code || 'http_error', message);
    }

    if (body.data === undefined) {
      throw new CareBotOnboardingError(
        'invalid_response',
        ERROR_CODE_MESSAGES.invalid_response,
        false
      );
    }

    return body.data;
  }

  async onboard(req: CareBotOnboardRequest): Promise<CareBotOnboardResponse> {
    if (this.useMock) {
      console.info('[CareBot mock] POST /api/carebot/onboard', req);
      await mockDelay();
      return buildMockOnboardResponse(req.assessmentId);
    }

    const data = await this.request<CareBotOnboardResponse>('/carebot/onboard', {
      method: 'POST',
      body: JSON.stringify(req),
    });
    assertOnboardShape(data);
    return data;
  }

  /**
   * 查询老人 CareBot 激活状态
   * Button mount 时调用，决定显示"一键开通"还是"已开通 · 查看二维码"。
   * Modal 完整数据（claimUrl 等）仍由 onboard 幂等再调返回。
   */
  async getOnboardingStatus(
    userId: string | number
  ): Promise<CareBotOnboardingStatus> {
    if (this.useMock) {
      console.info('[CareBot mock] GET /api/carebot/onboarding-status', { userId });
      await mockDelay(50, 150);
      return { activated: false };
    }

    const data = await this.request<CareBotOnboardingStatus>(
      `/carebot/onboarding-status?userId=${encodeURIComponent(String(userId))}`,
      { method: 'GET' }
    );
    return data;
  }

  /**
   * 发送老人手机号验证码（purpose=elder_carebot）
   * Rails 字段对齐：phoneNumber（与 /auth/send-sms 风格一致）
   */
  async sendElderSms(
    phoneNumber: string,
    userId: string | number
  ): Promise<CareBotSendSmsResponse> {
    if (this.useMock) {
      console.info('[CareBot mock] POST /api/carebot/send-sms', { userId, phoneNumber });
      await mockDelay(200, 400);
      return { ttlSeconds: 300, cooldownSeconds: 60 };
    }

    const data = await this.request<CareBotSendSmsResponse>('/carebot/send-sms', {
      method: 'POST',
      body: JSON.stringify({ userId, phoneNumber }),
    });
    return data;
  }

  async sendFamilyInvite(
    req: CareBotFamilyInviteRequest
  ): Promise<CareBotFamilyInviteResponse> {
    if (this.useMock) {
      console.info('[CareBot mock] POST /api/carebot/family-invite', req);
      await mockDelay();
      return buildMockFamilyInviteResponse(req.assessmentId, req.familyPhone);
    }

    const data = await this.request<CareBotFamilyInviteResponse>(
      '/carebot/family-invite',
      {
        method: 'POST',
        body: JSON.stringify(req),
      }
    );
    assertFamilyInviteShape(data);
    return data;
  }
}

export const carebotOnboardingService = new CarebotOnboardingService();
export default carebotOnboardingService;
